import {registerGraphNode} from "#graph/decorators/register-node"
import type {Evaluator} from "#graph/evaluators/types"
import {getPropertyNodeDefinition} from "#graph/nodes/get-property/definition"
import {GraphNode} from "#graph/nodes/graph/graph-node"
import {lazyDefinition} from "#graph/helpers/lazy-definition"
import {z} from "zod/v4-mini"

const definition = lazyDefinition(() => getPropertyNodeDefinition(z.any(), z.any(), z.any()))

@registerGraphNode
export class GetProperty<StructType extends {[x: string]: unknown}, KeyType extends string & keyof StructType, Context> extends GraphNode<
    StructType[KeyType],
    Context,
    {key: KeyType; value: StructType | GraphNode<StructType, Context>}
> {
    public override get definition() {
        return definition()
    }

    override run = async (evaluator: Evaluator<Context>): Promise<StructType[KeyType]> => {
        const key = this.parameters.key
        const value = await evaluator.get(this.parameters.value)
        const parsedValue = this.definition.validation.returns.safeParse(value?.[key])
        if (!parsedValue.success) {
            throw new Error(`Failed to parse GetProperty value for key "${key}": ${parsedValue.error.message} (${JSON.stringify(value?.[key])})`)
        }
        return parsedValue.data
    }

    override runSync = (evaluator: Evaluator<Context>): StructType[KeyType] => {
        const key = this.parameters.key
        const value = evaluator.getSync(this.parameters.value)
        return value[key]
    }
}
